// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import {
  CASE_DEPOSIT_PATTERN_ALLOW_VERY_HIGH_UPGRADE_FROM_MEDIUM,
  CASE_DEPOSIT_PATTERN_REVIEW_FAMILY,
  CASE_DEPOSIT_PATTERN_REVIEW_STATUS_DISMISSED,
  CASE_DEPOSIT_PATTERN_REVIEW_STATUS_REVIEWED,
  CASE_DEPOSIT_PATTERN_THRESHOLDS,
  CASE_ENTITY_KIND_EXCHANGE
} from './CaseBuddyAppConstants.js';
import { findCaseEntityCuratorMatch } from './CaseBuddyEntityCurators.js';
import { parseCaseContinuesAsHashes, rowHasFundsFollowedAndLeftBounds } from './CaseBuddyPathOperations.js';
import {
  abbreviateCaseBuddyHash,
  caseBuddyHashString,
  escapeRegExp,
  firstNonEmptyEntryValue,
  normalizeCell,
  normalizeComparisonKey,
  parseCaseUtcDate,
  rowHasMeaningfulData
} from './CaseBuddyShared.js';
import { parseCaseEntries } from './CaseBuddySheetData.js';
import { normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export const CASE_DEPOSIT_HOT_WALLET_VENDOR_MAX_LOOKUPS = 500;

export function caseDepositHotWalletNormalizeNoteForComparison(value = '') {
  return normalizeCell(value)
    .toLowerCase()
    .replace(/[.,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function caseDepositHotWalletIsPathEndsText(value = '') {
  const normalized = caseDepositHotWalletNormalizeNoteForComparison(value);
  return normalized === 'path ends';
}

export function caseDepositHotWalletNormalizeId(value = '') {
  const text = normalizeCell(value);
  if (!text) return '';
  if (/^0x/i.test(text)) return text.toLowerCase();
  if (/^[0-9a-f]+$/i.test(text)) return text.toLowerCase();
  return text;
}

export function caseDepositHotWalletIdsEqual(left = '', right = '') {
  const a = caseDepositHotWalletNormalizeId(left);
  const b = caseDepositHotWalletNormalizeId(right);
  return !!a && !!b && a === b;
}

export function caseDepositHotWalletStripEdgePunctuation(value = '') {
  return normalizeCell(value).replace(/^[\s.,;:()[\]{}<>"']+|[\s.,;:()[\]{}<>"']+$/g, '').trim();
}

export function caseDepositHotWalletExtractSingleContinuesTarget(value = '') {
  const source = normalizeCell(value);
  if (!/^continues\s+as\b/i.test(source)) return { ok: false, target: '' };
  const rest = source.replace(/^continues\s+as\b/i, '').trim();
  if (!rest) return { ok: false, target: '' };
  const targets = rest.split(',').map(caseDepositHotWalletStripEdgePunctuation).filter(Boolean);
  return targets.length === 1 ? { ok: true, target: targets[0] } : { ok: false, target: '' };
}

export function caseDepositHotWalletFirstNonEmptyEntryValue(rowRefs = [], colIndex = 0) {
  for (const rowRef of Array.isArray(rowRefs) ? rowRefs : []) {
    const value = normalizeCell(rowRef?.row?.[colIndex]);
    if (value) return value;
  }
  return '';
}

export function caseDepositHotWalletFindExchangeTerm(rawTerm = '', effective = {}) {
  const term = normalizeCell(rawTerm);
  if (!term) return null;
  const match = findCaseEntityCuratorMatch(effective, term);
  if (match?.isMatch && match.entityKind === CASE_ENTITY_KIND_EXCHANGE) {
    return {
      displayName: normalizeCell(match.matchedName || term),
      source: normalizeCell(match.source),
      matchedInput: term
    };
  }
  return null;
}

export function caseDepositHotWalletBuildNoteMatchers(effective = {}) {
  const exchangeItems = Array.isArray(effective.exchange) ? effective.exchange : [];
  return exchangeItems
    .map(item => normalizeCell(item?.name || item))
    .filter(Boolean)
    .sort((left, right) => right.length - left.length || left.localeCompare(right))
    .map((name) => ({
      name,
      pattern: new RegExp(`(^|[^A-Za-z0-9])(${escapeRegExp(name)})(?=$|[^A-Za-z0-9])`)
    }));
}

export function caseDepositHotWalletFindExchangeInNotes(notes = '', effective = {}) {
  const noteText = normalizeCell(notes);
  if (!noteText) return null;
  const matchers = caseDepositHotWalletBuildNoteMatchers(effective);
  for (const matcher of matchers) {
    const match = matcher.pattern.exec(noteText);
    if (!match) continue;
    const boundary = match[1] || '';
    const matchedText = match[2] || matcher.name;
    const startIndex = match.index + boundary.length;
    return {
      displayName: matcher.name,
      matchedText,
      startIndex,
      endIndex: startIndex + matchedText.length,
      source: 'notes'
    };
  }
  return null;
}

export function caseDepositHotWalletResolveRowExchange(row = [], effective = {}) {
  const attributionMatch = caseDepositHotWalletFindExchangeTerm(row?.[10], effective);
  if (attributionMatch) return { ...attributionMatch, sourceField: 'Column K attribution' };
  const noteMatch = caseDepositHotWalletFindExchangeInNotes(row?.[9], effective);
  if (noteMatch) return { ...noteMatch, sourceField: 'Column J notes' };
  return null;
}

export function caseDepositHotWalletResolveMatchedPairEntity(entry1Rows = [], entry2Rows = [], effective = {}) {
  const term1 = caseDepositHotWalletFirstNonEmptyEntryValue(entry1Rows, 10);
  const term2 = caseDepositHotWalletFirstNonEmptyEntryValue(entry2Rows, 10);
  const match1 = caseDepositHotWalletFindExchangeTerm(term1, effective);
  const match2 = caseDepositHotWalletFindExchangeTerm(term2, effective);
  if (!match1 || !match2) return null;
  if (match1.displayName !== match2.displayName) return null;
  return {
    displayName: match1.displayName,
    term1,
    term2,
    matchedViaAlias: match1.matchedInput !== match1.displayName || match2.matchedInput !== match2.displayName,
    source: [match1.source, match2.source].filter(Boolean).join(' / ')
  };
}

export function caseDepositHotWalletAllowedNoteNames(entity = {}) {
  return [entity.displayName, entity.term1, entity.term2]
    .map(caseDepositHotWalletNormalizeNoteForComparison)
    .filter(Boolean);
}

export function caseDepositHotWalletIsSafeDepositEntryNote(noteRaw = '', allowedNames = []) {
  const note = caseDepositHotWalletNormalizeNoteForComparison(noteRaw);
  if (!note) return true;
  if (note === 'deposit address pattern') return true;
  return allowedNames.some((name) => {
    if (!name) return false;
    return note === name ||
      note === `deposit address pattern ${name}` ||
      note === `${name} deposit` ||
      note === `${name} deposit address`;
  });
}

export function caseDepositHotWalletIsSafeHotWalletEntryNote(noteRaw = '', allowedNames = []) {
  const note = caseDepositHotWalletNormalizeNoteForComparison(noteRaw);
  if (!note) return false;
  if (note === 'path ends') return true;
  if (note === 'path ends hot wallet address pattern') return true;
  return allowedNames.some((name) => {
    if (!name) return false;
    return note === `${name} path ends` ||
      note === `${name} path ends hot wallet address pattern` ||
      note === `${name} hot wallet path ends` ||
      note === `${name} hot wallet path ends hot wallet address pattern`;
  });
}

export function caseDepositHotWalletNoteHasConcreteDepositOrHotWallet(notes = '') {
  const normalized = caseDepositHotWalletNormalizeNoteForComparison(notes);
  const withoutPatterns = normalized
    .replace(/deposit address pattern/g, '')
    .replace(/hot wallet address pattern/g, '')
    .replace(/hot wallet pattern/g, '')
    .trim();
  return /\bdeposit\b/.test(withoutPatterns) || /\bhot wallet\b/.test(withoutPatterns);
}

export function caseDepositHotWalletNoteIsAlreadyStandardized(notes = '', entityName = '') {
  const normalized = caseDepositHotWalletNormalizeNoteForComparison(notes);
  const entity = caseDepositHotWalletNormalizeNoteForComparison(entityName);
  if (!normalized || !entity) return false;
  return normalized === `${entity} deposit address` ||
    normalized === `${entity} deposit address path ends` ||
    normalized === `${entity} hot wallet path ends`;
}

export function caseDepositHotWalletInferClassificationFromNotes(notes = '') {
  const normalized = caseDepositHotWalletNormalizeNoteForComparison(notes);
  if (!normalized) return '';
  const withoutPatterns = normalized
    .replace(/deposit address pattern/g, '')
    .replace(/hot wallet address pattern/g, '')
    .replace(/hot wallet pattern/g, '')
    .trim();
  if (/\bhot wallet\b/.test(withoutPatterns)) return 'Hot Wallet';
  if (/\bdeposit\b/.test(withoutPatterns)) return 'Deposit Address';
  return '';
}

export function buildCaseDepositHotWalletStandardNote(existingNotes = '', entityName = '', classification = '') {
  const entity = normalizeCell(entityName);
  if (!entity) return normalizeCell(existingNotes);
  const normalizedClassification = classification === 'Hot Wallet' ? 'Hot Wallet' : 'Deposit Address';
  const existingHasPathEnds = /\bpath\s+ends\b/i.test(normalizeCell(existingNotes));
  const shouldEnd = normalizedClassification === 'Hot Wallet' || existingHasPathEnds;
  return `${entity} ${normalizedClassification}${shouldEnd ? '. Path ends.' : ''}`;
}


export const CASE_UNKNOWN_SERVICE_ENDPOINT_NOTE = 'Unknown. The receiving address had characteristics of a service address or exchange hot wallet, so the path was stopped here. Path ends.';

export function caseDepositHotWalletHasUnknownServiceEndpointMarker(notes = '') {
  const text = normalizeCell(notes);
  if (!text) return false;
  return /\b(?:\d+\s*k\+\s*txns[_\s-]*check[_\s-]*if[_\s-]*entity|check[_\s-]*if[_\s-]*entity)\b/i.test(text);
}

export function caseDepositHotWalletIsUnknownServiceEndpointStandardNote(notes = '') {
  const text = normalizeCell(notes);
  if (!text) return false;
  return /^unknown\./i.test(text) &&
    /\bpath\s+ends\b/i.test(text) &&
    /\bservice address\b/i.test(text) &&
    /\bexchange hot wallet\b/i.test(text);
}

export function buildCaseUnknownServiceEndpointNote() {
  return CASE_UNKNOWN_SERVICE_ENDPOINT_NOTE;
}

export function buildCaseDepositHotWalletCandidateSearchText(candidate = {}) {
  return [
    candidate.type,
    candidate.displayType,
    candidate.entityName,
    candidate.rowNum,
    candidate.entry1RowNum,
    candidate.entry2RowNum,
    candidate.pathLabel,
    candidate.entry1PathLabel,
    candidate.entry2PathLabel,
    candidate.hash,
    candidate.entry1Hash,
    candidate.entry2Hash,
    candidate.toAddress,
    candidate.currentNotes,
    candidate.entry1CurrentNotes,
    candidate.entry2CurrentNotes,
    candidate.proposedNotes,
    candidate.entry1ProposedNotes,
    candidate.entry2ProposedNotes,
    candidate.statusLabel,
    candidate.reason,
    candidate.vendorClassification
  ].map(part => normalizeCell(part).toLowerCase()).filter(Boolean).join(' ');
}

export function buildCaseDepositHotWalletPairCandidates(entries = [], effective = {}) {
  const candidates = [];
  for (let index = 0; index < entries.length - 1; index += 1) {
    const entry1 = entries[index];
    const entry2 = entries[index + 1];
    const rowRef1 = entry1?.rows?.[0];
    const rowRef2 = entry2?.rows?.[0];
    const row1 = rowRef1?.row || [];
    const row2 = rowRef2?.row || [];
    const hash1 = normalizeCell(row1[1] || entry1?.hash);
    const hash2 = normalizeCell(row2[1] || entry2?.hash);
    if (!hash1 || !hash2) continue;
    const target = caseDepositHotWalletExtractSingleContinuesTarget(row1[16]);
    if (!target.ok || !caseDepositHotWalletIdsEqual(target.target, hash2)) continue;
    if (!caseDepositHotWalletIsPathEndsText(row2[16])) continue;
    const entity = caseDepositHotWalletResolveMatchedPairEntity(entry1.rows, entry2.rows, effective);
    if (!entity) continue;
    const allowedNames = caseDepositHotWalletAllowedNoteNames(entity);
    const entry1Notes = normalizeCell(row1[9]);
    const entry2Notes = normalizeCell(row2[9]);
    const entry1Safe = caseDepositHotWalletIsSafeDepositEntryNote(entry1Notes, allowedNames);
    const entry2Safe = caseDepositHotWalletIsSafeHotWalletEntryNote(entry2Notes, allowedNames);
    if (!entry1Safe || !entry2Safe) continue;
    const entry1ProposedNotes = `${entity.displayName} Deposit Address`;
    const entry2ProposedNotes = `${entity.displayName} Hot Wallet. Path ends.`;
    const alreadyNormalized = normalizeCell(entry1Notes) === entry1ProposedNotes && normalizeCell(entry2Notes) === entry2ProposedNotes;
    const candidate = {
      key: `pair-${rowRef1.rowIndex}-${rowRef2.rowIndex}-${caseBuddyHashString(`${hash1}|${hash2}|${entity.displayName}`)}`,
      type: 'pair',
      displayType: 'Safe adjacent pair',
      entityName: entity.displayName,
      matchedViaAlias: !!entity.matchedViaAlias,
      entry1RowIndex: rowRef1.rowIndex,
      entry2RowIndex: rowRef2.rowIndex,
      entry1RowNum: rowRef1.rowIndex + 1,
      entry2RowNum: rowRef2.rowIndex + 1,
      entry1PathLabel: normalizeCell(row1[0] || entry1?.entryPathLabel),
      entry2PathLabel: normalizeCell(row2[0] || entry2?.entryPathLabel),
      entry1Hash: hash1,
      entry2Hash: hash2,
      entry1CurrentNotes: entry1Notes,
      entry2CurrentNotes: entry2Notes,
      entry1ProposedNotes,
      entry2ProposedNotes,
      isEligible: !alreadyNormalized,
      isAlreadyNormalized: alreadyNormalized,
      status: alreadyNormalized ? 'already-normalized' : 'eligible',
      statusLabel: alreadyNormalized ? 'Already normalized' : 'Ready for safe normalizer',
      reason: 'Adjacent entries continue from a deposit-address row into a path-ending hot-wallet row for the same known exchange/service.'
    };
    candidate.searchText = buildCaseDepositHotWalletCandidateSearchText(candidate);
    candidates.push(candidate);
  }
  return candidates;
}

export function buildCaseDepositHotWalletRowCleanupCandidates(rows = [], effective = {}, excludedRowIndexes = new Set()) {
  const candidates = [];
  rows.forEach((row, rowIndex) => {
    if (rowIndex === 0 || excludedRowIndexes.has(rowIndex) || !rowHasMeaningfulData(row)) return;
    const notes = normalizeCell(row?.[9]);
    if (!notes || caseDepositHotWalletHasUnknownServiceEndpointMarker(notes) || !caseDepositHotWalletNoteHasConcreteDepositOrHotWallet(notes)) return;
    const entity = caseDepositHotWalletResolveRowExchange(row, effective);
    if (!entity?.displayName) return;
    const classification = caseDepositHotWalletInferClassificationFromNotes(notes);
    if (!classification) return;
    const proposedNotes = buildCaseDepositHotWalletStandardNote(notes, entity.displayName, classification);
    if (!proposedNotes || proposedNotes === notes) return;
    const candidate = {
      key: `cleanup-${rowIndex}-${caseBuddyHashString(`${notes}|${entity.displayName}|${classification}`)}`,
      type: 'cleanup',
      displayType: 'Safe note cleanup',
      entityName: entity.displayName,
      rowIndex,
      rowNum: rowIndex + 1,
      pathLabel: normalizeCell(row?.[0]),
      hash: normalizeCell(row?.[1]),
      toAddress: normalizeCell(row?.[3]),
      currentNotes: notes,
      proposedNotes,
      vendorClassification: classification,
      isEligible: true,
      isAlreadyNormalized: false,
      status: 'eligible',
      statusLabel: `Normalize as ${classification}`,
      reason: 'Column J already asserts deposit/hot-wallet wording, but the phrase is not in the preferred Case Buddy format.'
    };
    candidate.searchText = buildCaseDepositHotWalletCandidateSearchText(candidate);
    candidates.push(candidate);
  });
  return candidates;
}


export function buildCaseUnknownServiceEndpointCleanupCandidates(rows = [], excludedRowIndexes = new Set()) {
  const candidates = [];
  rows.forEach((row, rowIndex) => {
    if (rowIndex === 0 || excludedRowIndexes.has(rowIndex) || !rowHasMeaningfulData(row)) return;
    const notes = normalizeCell(row?.[9]);
    if (!notes || !caseDepositHotWalletHasUnknownServiceEndpointMarker(notes)) return;
    const proposedNotes = buildCaseUnknownServiceEndpointNote(notes);
    const alreadyNormalized = caseDepositHotWalletIsUnknownServiceEndpointStandardNote(notes) && normalizeCell(notes) === proposedNotes;
    if (alreadyNormalized || proposedNotes === notes) return;
    const candidate = {
      key: `unknown-service-${rowIndex}-${caseBuddyHashString(notes)}`,
      type: 'unknown_service_endpoint',
      displayType: 'Unknown service endpoint cleanup',
      entityName: 'Unknown service-style endpoint',
      rowIndex,
      rowNum: rowIndex + 1,
      pathLabel: normalizeCell(row?.[0]),
      hash: normalizeCell(row?.[1]),
      toAddress: normalizeCell(row?.[3]),
      currentNotes: notes,
      proposedNotes,
      vendorClassification: 'Unknown',
      isEligible: true,
      isAlreadyNormalized: false,
      status: 'eligible',
      statusLabel: 'Rewrite generic system marker',
      reason: 'Column J contains the generic 50k+TXNS_Check_If_Entity marker. Replace it with an informative unknown service/hot-wallet path-ending note before What Went Where.'
    };
    candidate.searchText = buildCaseDepositHotWalletCandidateSearchText(candidate);
    candidates.push(candidate);
  });
  return candidates;
}

export function buildCaseDepositHotWalletVendorCandidates(rows = [], effective = {}, vendorResultsByKey = {}, excludedKeys = new Set()) {
  const candidates = [];
  rows.forEach((row, rowIndex) => {
    if (rowIndex === 0 || !rowHasMeaningfulData(row)) return;
    const notes = normalizeCell(row?.[9]);
    const toAddress = normalizeCell(row?.[3]);
    if (!notes || caseDepositHotWalletHasUnknownServiceEndpointMarker(notes) || !toAddress) return;
    const entity = caseDepositHotWalletResolveRowExchange(row, effective);
    if (!entity?.displayName) return;
    const concrete = caseDepositHotWalletNoteHasConcreteDepositOrHotWallet(notes);
    const patternOnly = /\b(?:deposit address pattern|hot wallet address pattern|hot wallet pattern)\b/i.test(notes);
    if (concrete && !patternOnly && caseDepositHotWalletNoteIsAlreadyStandardized(notes, entity.displayName)) return;
    if (concrete && !patternOnly) return;
    const key = `vendor-${rowIndex}-${caseBuddyHashString(`${toAddress}|${entity.displayName}|${notes}`)}`;
    if (excludedKeys.has(key)) return;
    const vendorResult = vendorResultsByKey?.[key] || null;
    const classification = normalizeCell(vendorResult?.classification);
    const proposedNotes = classification ? buildCaseDepositHotWalletStandardNote(notes, entity.displayName, classification) : '';
    const candidate = {
      key,
      type: 'vendor',
      displayType: 'Arkham lookup candidate',
      entityName: entity.displayName,
      rowIndex,
      rowNum: rowIndex + 1,
      pathLabel: normalizeCell(row?.[0]),
      hash: normalizeCell(row?.[1]),
      toAddress,
      currentNotes: notes,
      proposedNotes,
      vendorClassification: classification,
      vendorResult,
      isEligible: !!classification && !!proposedNotes && proposedNotes !== notes,
      isAlreadyNormalized: caseDepositHotWalletNoteIsAlreadyStandardized(notes, entity.displayName),
      status: classification ? 'confirmed' : 'needs-lookup',
      statusLabel: classification ? `Confirmed ${classification}` : 'Needs vendor lookup',
      reason: patternOnly
        ? 'Column J contains a deposit/hot-wallet pattern hint. Use the vendor lookup to confirm before normalizing.'
        : 'Column J contains a known exchange/service but does not yet say Deposit Address or Hot Wallet.'
    };
    candidate.searchText = buildCaseDepositHotWalletCandidateSearchText(candidate);
    candidates.push(candidate);
  });
  return candidates;
}

export function buildCaseDepositHotWalletReviewAnalysis(mainData = [], effective = {}, workflowState = {}, vendorResultsByKey = {}) {
  const rows = normalizeCaseMatrix(mainData);
  const entries = parseCaseEntries(rows);
  const pairCandidates = buildCaseDepositHotWalletPairCandidates(entries, effective);
  const pairRowIndexes = new Set();
  pairCandidates.forEach((candidate) => {
    if (candidate.isEligible) {
      pairRowIndexes.add(candidate.entry1RowIndex);
      pairRowIndexes.add(candidate.entry2RowIndex);
    }
  });
  const cleanupCandidates = buildCaseDepositHotWalletRowCleanupCandidates(rows, effective, pairRowIndexes);
  const unknownServiceCandidates = buildCaseUnknownServiceEndpointCleanupCandidates(rows, pairRowIndexes);
  const localCandidates = [...pairCandidates, ...cleanupCandidates, ...unknownServiceCandidates];
  const vendorCandidates = buildCaseDepositHotWalletVendorCandidates(rows, effective, vendorResultsByKey);
  const eligibleLocalCandidates = localCandidates.filter(candidate => candidate.isEligible);
  const confirmedVendorCandidates = vendorCandidates.filter(candidate => candidate.isEligible);
  const basisPayload = eligibleLocalCandidates.map(candidate => ({
    key: candidate.key,
    type: candidate.type,
    entityName: candidate.entityName,
    rowNum: candidate.rowNum,
    entry1RowNum: candidate.entry1RowNum,
    entry2RowNum: candidate.entry2RowNum,
    proposedNotes: candidate.proposedNotes,
    entry1ProposedNotes: candidate.entry1ProposedNotes,
    entry2ProposedNotes: candidate.entry2ProposedNotes
  }));
  const basisHash = `deposit-hot-wallet-${caseBuddyHashString(JSON.stringify(basisPayload))}`;
  const reviewRecord = workflowState?.depositHotWalletReview && typeof workflowState.depositHotWalletReview === 'object'
    ? workflowState.depositHotWalletReview
    : null;
  const isCurrent = !!reviewRecord?.completedAtUtc && reviewRecord.basisHash === basisHash;
  const summary = eligibleLocalCandidates.length > 0
    ? `${eligibleLocalCandidates.length} safe note normalization candidate${eligibleLocalCandidates.length === 1 ? '' : 's'} remain.`
    : vendorCandidates.length > 0
      ? `No safe local normalizations remain. ${vendorCandidates.length} optional Arkham lookup candidate${vendorCandidates.length === 1 ? '' : 's'} can be reviewed if desired.`
      : 'No safe note normalization candidates remain.';
  return {
    rowsAnalyzed: rows.filter(rowHasMeaningfulData).length,
    entriesAnalyzed: entries.length,
    pairCandidates,
    cleanupCandidates,
    unknownServiceCandidates,
    localCandidates,
    vendorCandidates,
    allCandidates: [...localCandidates, ...vendorCandidates],
    eligibleLocalCount: eligibleLocalCandidates.length,
    alreadyNormalizedCount: localCandidates.filter(candidate => candidate.isAlreadyNormalized).length,
    vendorCandidateCount: vendorCandidates.length,
    confirmedVendorCount: confirmedVendorCandidates.length,
    basisHash,
    isCurrent,
    summary
  };
}

export function buildCaseDepositHotWalletAuditRows(analysis = {}, actionLabel = '') {
  const rows = [
    ['Case Buddy Deposit / Hot Wallet Notes Audit', '', '', '', '', '', '', '', '', 'Generated by Normalize deposit and hot-wallet notes. Audit only; Main Data is the source of truth.'],
    ['Action', actionLabel || 'Review refreshed', 'Rows Analyzed', analysis.rowsAnalyzed || 0, 'Safe Candidates', analysis.eligibleLocalCount || 0, 'Vendor Candidates', analysis.vendorCandidateCount || 0, 'Confirmed Vendor', analysis.confirmedVendorCount || 0],
    ['Type', 'Status', 'Entity', 'Row(s)', 'Hash', 'To Address', 'Current Notes', 'Proposed Notes', 'Reason', 'Vendor Result']
  ];
  (analysis.allCandidates || []).forEach((candidate) => {
    rows.push([
      candidate.displayType || candidate.type || '',
      candidate.statusLabel || '',
      candidate.entityName || '',
      candidate.type === 'pair'
        ? `${candidate.entry1RowNum || ''} -> ${candidate.entry2RowNum || ''}`
        : candidate.rowNum || '',
      candidate.hash || candidate.entry1Hash || '',
      candidate.toAddress || '',
      candidate.currentNotes || [candidate.entry1CurrentNotes, candidate.entry2CurrentNotes].filter(Boolean).join(' | '),
      candidate.proposedNotes || [candidate.entry1ProposedNotes, candidate.entry2ProposedNotes].filter(Boolean).join(' | '),
      candidate.reason || '',
      candidate.vendorClassification || ''
    ]);
  });
  return rows;
}

export function caseDepositHotWalletExtractStringsDeep(value, bucket = [], depth = 0) {
  if (depth > 12 || bucket.length > 2000 || value == null) return;
  if (typeof value === 'string') {
    const text = value.trim();
    if (text) bucket.push(text);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach(item => caseDepositHotWalletExtractStringsDeep(item, bucket, depth + 1));
    return;
  }
  if (typeof value === 'object') {
    Object.values(value).forEach(item => caseDepositHotWalletExtractStringsDeep(item, bucket, depth + 1));
  }
}

export function classifyCaseDepositHotWalletVendorResult(result = {}) {
  const bucket = [];
  [
    result?.label,
    result?.name,
    result?.entity,
    result?.classification,
    result?.category,
    result?.type,
    result?.subtype,
    result?.response_text,
    result?.searchable_text,
    result?.raw_response,
    result?.raw_json,
    result?.arkham_response,
    result?.vendor_response,
    result?.data
  ].forEach(value => caseDepositHotWalletExtractStringsDeep(value, bucket));
  const searchText = bucket.join(' \n ').toLowerCase();
  if (!searchText) return '';
  if (/hot\s*wallet/i.test(searchText)) return 'Hot Wallet';
  if (/deposit/i.test(searchText)) return 'Deposit Address';
  return '';
}

export function caseDepositPatternGradeRank(grade = '') {
  const normalized = normalizeCell(grade).toUpperCase();
  if (normalized === 'VERY_HIGH') return 5;
  if (normalized === 'HIGH') return 4;
  if (normalized === 'MEDIUM') return 3;
  if (normalized === 'LOW') return 2;
  return 1;
}

export function caseDepositPatternIsRequiredGrade(grade = '') {
  return caseDepositPatternGradeRank(grade) >= caseDepositPatternGradeRank('HIGH');
}

export function caseDepositPatternParseAmount(value = '') {
  const cleaned = normalizeCell(value).replace(/,/g, '');
  if (!cleaned) return null;
  const amount = Number.parseFloat(cleaned);
  return Number.isFinite(amount) ? amount : null;
}

export function caseDepositPatternFormatAmount(value = 0) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '';
  return amount.toFixed(8).replace(/\.?0+$/, '');
}

export function caseDepositPatternTokenEquals(left = '', right = '') {
  const a = normalizeCell(left).toLowerCase();
  const b = normalizeCell(right).toLowerCase();
  return !!a && a === b;
}

export function caseDepositPatternContainsWholeWord(text = '', word = '') {
  const source = String(text || '');
  const token = normalizeCell(word);
  if (!source || !token) return false;
  return new RegExp(`\\b${escapeRegExp(token)}\\b`, 'i').test(source);
}

export function caseDepositPatternContainsPhrase(text = '', phrase = '') {
  return normalizeCell(text).toLowerCase().includes(normalizeCell(phrase).toLowerCase());
}

export function caseDepositPatternCleanAddressOnlyCandidate(text = '') {
  return normalizeCell(text)
    .replace(/^[\s"'([{<]+/, '')
    .replace(/[\s"'\])}>]+$/, '')
    .replace(/[.,;:]+$/, '')
    .trim();
}

export function caseDepositPatternLooksLikeAddressOnly(text = '') {
  const cleaned = caseDepositPatternCleanAddressOnlyCandidate(text);
  return /^0x[a-fA-F0-9]{40}$/.test(cleaned) || /^T[1-9A-HJ-NP-Za-km-z]{25,60}$/.test(cleaned);
}

export function caseDepositPatternEntryHasWholeWord(entry = {}, word = '') {
  return (Array.isArray(entry.rows) ? entry.rows : []).some((item) =>
    caseDepositPatternContainsWholeWord(item?.row?.[9], word)
  );
}

export function caseDepositPatternEntryHasPhrase(entry = {}, phrase = '') {
  return (Array.isArray(entry.rows) ? entry.rows : []).some((item) =>
    caseDepositPatternContainsPhrase(item?.row?.[9], phrase) || caseDepositPatternContainsPhrase(item?.row?.[16], phrase)
  );
}

export function caseDepositPatternEntryHasPathEnds(entry = {}) {
  return (Array.isArray(entry.rows) ? entry.rows : []).some((item) =>
    /\bpath\s+ends\b/i.test(normalizeCell(item?.row?.[9])) || /\bpath\s+ends\b/i.test(normalizeCell(item?.row?.[16]))
  );
}

export function caseDepositPatternEntryHasSwapOrBridgeNote(entry = {}) {
  return (Array.isArray(entry.rows) ? entry.rows : []).some((item) => {
    const row = Array.isArray(item?.row) ? item.row : [];
    const notes = [row?.[9], row?.[11]].map(normalizeCell).filter(Boolean).join(' | ');
    return /\b(?:swapped|bridged)\b/i.test(notes);
  });
}

export function caseDepositPatternCollectUniqueEntryAddresses(entry = {}, columnIndex = 2) {
  const seen = new Map();
  (Array.isArray(entry.rows) ? entry.rows : []).forEach((item) => {
    const row = Array.isArray(item?.row) ? item.row : [];
    const address = normalizeCell(row?.[columnIndex]);
    const key = normalizeComparisonKey(address);
    if (!key || seen.has(key)) return;
    seen.set(key, address);
  });
  return [...seen.entries()].map(([key, address]) => ({ key, address }));
}

export function caseDepositPatternEntryIsSingleSelfTransfer(entry = {}) {
  const fromAddresses = caseDepositPatternCollectUniqueEntryAddresses(entry, 2);
  const toAddresses = caseDepositPatternCollectUniqueEntryAddresses(entry, 3);
  return fromAddresses.length === 1 && toAddresses.length === 1 && fromAddresses[0].key === toAddresses[0].key;
}

export function caseDepositPatternShouldOmitSwapBridgeSelfTransferPair(parent = {}, child = {}) {
  const pairHasSwapOrBridgeNote = caseDepositPatternEntryHasSwapOrBridgeNote(parent) || caseDepositPatternEntryHasSwapOrBridgeNote(child);
  if (!pairHasSwapOrBridgeNote) return false;
  return caseDepositPatternEntryIsSingleSelfTransfer(parent) || caseDepositPatternEntryIsSingleSelfTransfer(child);
}

export function caseDepositPatternCollectEntryOutputs(entry = {}) {
  const outputs = [];
  (Array.isArray(entry.rows) ? entry.rows : []).forEach((item) => {
    const row = Array.isArray(item?.row) ? item.row : [];
    const amount = caseDepositPatternParseAmount(row?.[5]);
    const token = normalizeCell(row?.[6]);
    if (amount == null || !token) return;
    outputs.push({
      amount,
      token,
      rowIndex: item.rowIndex,
      row,
      toAddress: normalizeCell(row?.[3]),
      fundsFollowed: normalizeCell(row?.[12]),
      leftBoundsDisplay: normalizeCell(row?.[17]),
      leftBoundsNumeric: normalizeCell(row?.[18])
    });
  });
  return outputs;
}

export function buildCaseDepositPatternEntryRecords(mainData = []) {
  const data = normalizeCaseMatrix(mainData);
  const entries = parseCaseEntries(data);
  const records = [];
  const mapByHash = new Map();
  const duplicateHashes = [];
  let missingTimestampCount = 0;
  let invalidTimestampCount = 0;

  entries.forEach((entry) => {
    const firstRow = Array.isArray(entry.rows?.[0]?.row) ? entry.rows[0].row : [];
    const dateRaw = firstNonEmptyEntryValue(entry.rows, 4) || normalizeCell(firstRow?.[4]);
    const dateInfo = parseCaseUtcDate(dateRaw);
    if (!dateInfo.hasValue) missingTimestampCount += 1;
    else if (!dateInfo.isValid) invalidTimestampCount += 1;

    const hash = normalizeCell(entry.hash || firstRow?.[1]);
    const normalizedHash = normalizeComparisonKey(hash);
    const firstNotes = normalizeCell(firstRow?.[9]);
    const firstAnalystNotes = normalizeCell(firstRow?.[11]);
    const combinedNotes = (Array.isArray(entry.rows) ? entry.rows : [])
      .flatMap(item => [normalizeCell(item?.row?.[9]), normalizeCell(item?.row?.[11])])
      .filter(Boolean)
      .join(' | ');

    const record = {
      anchorRowId: `row-${(entry.startRow || 0) + 1}-${caseBuddyHashString(hash || String(entry.startRow || 0))}`,
      hash,
      normalizedHash,
      shortHash: abbreviateCaseBuddyHash(hash),
      startRow: entry.startRow,
      rowNumber: (entry.startRow || 0) + 1,
      rows: Array.isArray(entry.rows) ? entry.rows : [],
      firstRow,
      pathLabel: firstNonEmptyEntryValue(entry.rows, 0) || normalizeCell(firstRow?.[0]),
      dateRaw,
      dateInfo,
      timeOk: !!dateInfo.isValid,
      timeMs: dateInfo.isValid ? dateInfo.timestamp : null,
      fromHash: normalizeCell(firstRow?.[14]),
      qText: normalizeCell(firstRow?.[16]),
      firstNotes,
      firstAnalystNotes,
      combinedNotes,
      attribution: firstNonEmptyEntryValue(entry.rows, 10) || normalizeCell(firstRow?.[10]),
      toAddress: firstNonEmptyEntryValue(entry.rows, 3) || normalizeCell(firstRow?.[3]),
      outputs: caseDepositPatternCollectEntryOutputs(entry),
      pathEnds: caseDepositPatternEntryHasPathEnds(entry),
      depositInJ: caseDepositPatternEntryHasWholeWord(entry, 'deposit'),
      branchesAs: caseDepositPatternEntryHasPhrase(entry, 'branches as'),
      hotWallet: caseDepositPatternEntryHasPhrase(entry, 'hot wallet'),
      swapOrBridgeNote: caseDepositPatternEntryHasSwapOrBridgeNote(entry),
      singleSelfTransfer: caseDepositPatternEntryIsSingleSelfTransfer(entry),
      addressOnlyInJ: caseDepositPatternLooksLikeAddressOnly(firstNotes),
      continuesAs: parseCaseContinuesAsHashes(firstRow?.[16])
    };

    records.push(record);
    if (normalizedHash) {
      if (mapByHash.has(normalizedHash)) duplicateHashes.push(hash);
      else mapByHash.set(normalizedHash, record);
    }
  });

  return { records, mapByHash, duplicateHashes, missingTimestampCount, invalidTimestampCount };
}

export function buildCaseDepositPatternEdges(entries = [], mapByHash = new Map()) {
  const edges = [];
  const seen = new Set();

  (Array.isArray(entries) ? entries : []).forEach((parent) => {
    (Array.isArray(parent.continuesAs) ? parent.continuesAs : []).forEach((childHashRaw) => {
      const childHash = normalizeComparisonKey(childHashRaw);
      const child = mapByHash.get(childHash);
      if (!child) return;
      const key = `${parent.normalizedHash}-->${child.normalizedHash}`;
      if (seen.has(key)) return;
      seen.add(key);
      edges.push({ parent, child, via: 'Column Q' });
    });
  });

  (Array.isArray(entries) ? entries : []).forEach((child) => {
    const parentHash = normalizeComparisonKey(child.fromHash);
    const parent = mapByHash.get(parentHash);
    if (!parent) return;
    const key = `${parent.normalizedHash}-->${child.normalizedHash}`;
    if (seen.has(key)) return;
    seen.add(key);
    edges.push({ parent, child, via: 'Column O' });
  });

  return edges;
}

export function computeCaseDepositPatternBestAmountMatch(parent = {}, child = {}) {
  let best = null;
  (Array.isArray(parent.outputs) ? parent.outputs : []).forEach((parentOutput) => {
    (Array.isArray(child.outputs) ? child.outputs : []).forEach((childOutput) => {
      if (!caseDepositPatternTokenEquals(parentOutput.token, childOutput.token)) return;
      if (!(parentOutput.amount > 0) || !(childOutput.amount > 0)) return;
      const minAmount = Math.min(parentOutput.amount, childOutput.amount);
      const maxAmount = Math.max(parentOutput.amount, childOutput.amount);
      const similarity = maxAmount > 0 ? minAmount / maxAmount : 0;
      if (!best || similarity > best.similarity) {
        best = {
          token: parentOutput.token,
          amountA: parentOutput.amount,
          amountB: childOutput.amount,
          similarity,
          parentOutput,
          childOutput,
          amountAFormatted: caseDepositPatternFormatAmount(parentOutput.amount),
          amountBFormatted: caseDepositPatternFormatAmount(childOutput.amount)
        };
      }
    });
  });
  return best;
}

export function gradeCaseDepositPatternMatch(deltaMinutes = 0, similarity = 0) {
  const deltaHours = deltaMinutes / 60;
  if (deltaMinutes <= CASE_DEPOSIT_PATTERN_THRESHOLDS.highMinutes && similarity >= CASE_DEPOSIT_PATTERN_THRESHOLDS.exactSimilarity) return 'HIGH';
  if (deltaHours <= CASE_DEPOSIT_PATTERN_THRESHOLDS.mediumHours && similarity >= CASE_DEPOSIT_PATTERN_THRESHOLDS.exactSimilarity) return 'MEDIUM';
  if (deltaHours <= CASE_DEPOSIT_PATTERN_THRESHOLDS.lowHours && similarity >= CASE_DEPOSIT_PATTERN_THRESHOLDS.lowSimilarity) return 'LOW';
  if (deltaHours <= CASE_DEPOSIT_PATTERN_THRESHOLDS.veryLowHours && similarity >= CASE_DEPOSIT_PATTERN_THRESHOLDS.veryLowSimilarity) return 'VERY_LOW';
  return '';
}

export function caseDepositPatternShouldUpgradeToVeryHigh(baseGrade = '', signals = []) {
  const okBase = baseGrade === 'HIGH' || (CASE_DEPOSIT_PATTERN_ALLOW_VERY_HIGH_UPGRADE_FROM_MEDIUM && baseGrade === 'MEDIUM');
  return okBase && Array.isArray(signals) && signals.length > 0;
}

export function buildCaseDepositPatternReviewBasisHash(payload = {}) {
  const stablePayload = {
    parentAnchorRowId: payload.parentAnchorRowId || '',
    childAnchorRowId: payload.childAnchorRowId || '',
    parentHash: payload.parentHash || '',
    childHash: payload.childHash || '',
    token: payload.token || '',
    amountA: payload.amountAFormatted || '',
    amountB: payload.amountBFormatted || '',
    deltaMinutes: Number(payload.deltaMinutes || 0).toFixed(6),
    grade: payload.grade || '',
    signals: Array.isArray(payload.signals) ? payload.signals.slice().sort() : []
  };
  return `deposit-pattern-${caseBuddyHashString(JSON.stringify(stablePayload))}`;
}

export function buildCaseDepositPatternReviewKey(caseId = '', parentAnchorRowId = '', childAnchorRowId = '', basisHash = '') {
  return [normalizeCell(caseId).toLowerCase(), CASE_DEPOSIT_PATTERN_REVIEW_FAMILY, normalizeCell(parentAnchorRowId).toLowerCase(), normalizeCell(childAnchorRowId).toLowerCase(), normalizeCell(basisHash).toLowerCase()].join('|');
}

export function normalizeCaseDepositPatternReviewStatus(value = '') {
  const status = normalizeCell(value).toLowerCase();
  return status === CASE_DEPOSIT_PATTERN_REVIEW_STATUS_DISMISSED
    ? CASE_DEPOSIT_PATTERN_REVIEW_STATUS_DISMISSED
    : CASE_DEPOSIT_PATTERN_REVIEW_STATUS_REVIEWED;
}

export function caseDepositPatternStatusIsResolved(value = '') {
  const status = normalizeCell(value).toLowerCase();
  return status === CASE_DEPOSIT_PATTERN_REVIEW_STATUS_REVIEWED || status === CASE_DEPOSIT_PATTERN_REVIEW_STATUS_DISMISSED;
}

export function buildCaseDepositPatternSearchText(candidate = {}) {
  return [
    candidate.grade,
    candidate.parentPathLabel,
    candidate.parentHash,
    candidate.parentNotes,
    candidate.childPathLabel,
    candidate.childHash,
    candidate.childNotes,
    candidate.childAttribution,
    candidate.childAnalystNotes,
    candidate.childFromHash,
    candidate.childQ,
    candidate.token,
    ...(candidate.veryHighSignals || [])
  ].map(value => normalizeCell(value).toLowerCase()).join(' ');
}

export function buildCaseDepositPatternReviewAnalysis(mainData = [], workflowState = {}, options = {}) {
  const caseId = normalizeCell(options.caseId || 'case');
  const reviewState = workflowState?.depositPatternReview && typeof workflowState.depositPatternReview === 'object'
    ? workflowState.depositPatternReview
    : {};
  const decisionsByKey = reviewState.decisionsByKey && typeof reviewState.decisionsByKey === 'object'
    ? reviewState.decisionsByKey
    : {};
  const parsed = buildCaseDepositPatternEntryRecords(mainData);
  const edges = buildCaseDepositPatternEdges(parsed.records, parsed.mapByHash);
  const candidates = [];
  let skippedEdgesNoTimeCount = 0;
  let skippedSwapBridgeSelfTransferCount = 0;

  edges.forEach((edge) => {
    const parent = edge.parent;
    const child = edge.child;
    if (!parent || !child) return;
    if (child.pathEnds) return;
    if (caseDepositPatternShouldOmitSwapBridgeSelfTransferPair(parent, child)) {
      skippedSwapBridgeSelfTransferCount += 1;
      return;
    }
    if (!parent.timeOk || !child.timeOk) {
      skippedEdgesNoTimeCount += 1;
      return;
    }

    const best = computeCaseDepositPatternBestAmountMatch(parent, child);
    if (!best) return;

    const deltaMinutes = Math.abs((child.timeMs || 0) - (parent.timeMs || 0)) / 60000;
    const baseGrade = gradeCaseDepositPatternMatch(deltaMinutes, best.similarity);
    if (!baseGrade) return;

    const signals = [];
    const missingSignals = [];
    const addSignal = (present, label) => {
      if (present) signals.push(label);
      else missingSignals.push(label);
    };
    addSignal(parent.depositInJ, 'deposit');
    addSignal(child.branchesAs, 'branches_as');
    addSignal(child.addressOnlyInJ, 'addr_note');
    addSignal(child.hotWallet, 'hot_wallet');

    const upgradedToVeryHigh = caseDepositPatternShouldUpgradeToVeryHigh(baseGrade, signals);
    const grade = upgradedToVeryHigh ? 'VERY_HIGH' : baseGrade;
    const childOutputRowIndex = Number.isInteger(best.childOutput?.rowIndex) ? best.childOutput.rowIndex : child.startRow;
    const childOutputRow = Array.isArray(best.childOutput?.row) ? best.childOutput.row : child.firstRow;
    const parentOutputRowIndex = Number.isInteger(best.parentOutput?.rowIndex) ? best.parentOutput.rowIndex : parent.startRow;
    const basisHash = buildCaseDepositPatternReviewBasisHash({
      parentAnchorRowId: parent.anchorRowId,
      childAnchorRowId: child.anchorRowId,
      parentHash: parent.hash,
      childHash: child.hash,
      token: best.token,
      amountAFormatted: best.amountAFormatted,
      amountBFormatted: best.amountBFormatted,
      deltaMinutes,
      grade,
      signals
    });
    const reviewKey = buildCaseDepositPatternReviewKey(caseId, parent.anchorRowId, child.anchorRowId, basisHash);
    const decision = decisionsByKey[reviewKey] && typeof decisionsByKey[reviewKey] === 'object' ? decisionsByKey[reviewKey] : null;
    const currentReviewStatus = decision?.status || '';
    const hasCurrentDecision = !!decision && normalizeCell(decision.basisHash) === basisHash && caseDepositPatternStatusIsResolved(currentReviewStatus);
    const isRequired = caseDepositPatternIsRequiredGrade(grade);
    const deltaHours = deltaMinutes / 60;
    const candidate = {
      key: `${parent.anchorRowId}|${child.anchorRowId}`,
      reviewKey,
      reviewBasisHash: basisHash,
      grade,
      baseGrade,
      upgradedToVeryHigh,
      veryHighSignals: signals,
      veryHighMissing: missingSignals,
      isRequired,
      parentAnchorRowId: parent.anchorRowId,
      parentHash: parent.hash,
      parentShortHash: parent.shortHash,
      parentPathLabel: parent.pathLabel,
      parentRowNumber: parent.rowNumber,
      parentOutputRowIndex,
      parentOutputRowNumber: parentOutputRowIndex + 1,
      parentNotes: parent.firstNotes,
      childAnchorRowId: child.anchorRowId,
      childHash: child.hash,
      childShortHash: child.shortHash,
      childPathLabel: child.pathLabel,
      childRowNumber: child.rowNumber,
      childOutputRowIndex,
      childOutputRowNumber: childOutputRowIndex + 1,
      childNotes: child.firstNotes,
      childAttribution: child.attribution,
      childAnalystNotes: child.firstAnalystNotes,
      childAttributionAndAnalystNotes: [child.attribution ? `Column K: ${child.attribution}` : '', child.firstAnalystNotes ? `Column L: ${child.firstAnalystNotes}` : ''].filter(Boolean).join('\n'),
      childFromHash: child.fromHash,
      childQ: child.qText,
      childToAddress: child.toAddress || normalizeCell(childOutputRow?.[3]),
      childPathOperationEligible: rowHasFundsFollowedAndLeftBounds(childOutputRow),
      via: edge.via,
      token: best.token,
      deltaMinutes,
      deltaHours,
      deltaMinutesFormatted: deltaMinutes.toFixed(2),
      deltaHoursFormatted: deltaHours.toFixed(2),
      amountSimilarity: best.similarity,
      amountSimilarityPercent: `${(best.similarity * 100).toFixed(2)}%`,
      amountAFormatted: best.amountAFormatted,
      amountBFormatted: best.amountBFormatted,
      hasCurrentDecision,
      currentReviewStatus: normalizeCaseDepositPatternReviewStatus(currentReviewStatus),
      currentReviewStatusLabel: normalizeCaseDepositPatternReviewStatus(currentReviewStatus) === CASE_DEPOSIT_PATTERN_REVIEW_STATUS_DISMISSED ? 'Dismissed' : 'Reviewed',
      decision,
      decisionComment: normalizeCell(decision?.comment),
      searchText: ''
    };
    candidate.searchText = buildCaseDepositPatternSearchText(candidate);
    candidates.push(candidate);
  });

  candidates.sort((left, right) => {
    if (left.hasCurrentDecision !== right.hasCurrentDecision) return left.hasCurrentDecision ? 1 : -1;
    const rankCompare = caseDepositPatternGradeRank(right.grade) - caseDepositPatternGradeRank(left.grade);
    if (rankCompare !== 0) return rankCompare;
    if (left.deltaMinutes !== right.deltaMinutes) return left.deltaMinutes - right.deltaMinutes;
    return (left.childOutputRowIndex || 0) - (right.childOutputRowIndex || 0);
  });

  const requiredCandidates = candidates.filter(candidate => candidate.isRequired);
  const requiredReviewed = requiredCandidates.filter(candidate => candidate.hasCurrentDecision);
  const pendingRequired = requiredCandidates.filter(candidate => !candidate.hasCurrentDecision);
  const pendingCandidates = candidates.filter(candidate => !candidate.hasCurrentDecision);
  const basisPayload = candidates.map(candidate => ({ reviewKey: candidate.reviewKey, basisHash: candidate.reviewBasisHash, grade: candidate.grade })).sort((a, b) => a.reviewKey.localeCompare(b.reviewKey));
  const currentBasisHash = `deposit-pattern-review-${caseBuddyHashString(JSON.stringify(basisPayload))}`;
  const completedBasisHash = normalizeCell(reviewState.completedBasisHash);
  const isCurrent = !!completedBasisHash && completedBasisHash === currentBasisHash;
  let summary = '';

  if (requiredCandidates.length === 0) {
    summary = candidates.length === 0
      ? 'No deposit-pattern / early path-end candidates were found.'
      : `No HIGH or VERY_HIGH candidates require review. ${candidates.length} optional lower-priority candidate${candidates.length === 1 ? '' : 's'} remain visible for analyst discretion.`;
  } else {
    summary = `${requiredReviewed.length}/${requiredCandidates.length} HIGH/VERY_HIGH candidate${requiredCandidates.length === 1 ? '' : 's'} reviewed or dismissed.`;
  }

  if (skippedSwapBridgeSelfTransferCount > 0) {
    summary = `${summary} ${skippedSwapBridgeSelfTransferCount} swap/bridge self-transfer edge${skippedSwapBridgeSelfTransferCount === 1 ? '' : 's'} omitted.`;
  }

  return {
    entriesParsed: parsed.records.length,
    edgesChecked: edges.length,
    totalCandidateCount: candidates.length,
    requiredCandidateCount: requiredCandidates.length,
    requiredReviewedCount: requiredReviewed.length,
    pendingRequiredCount: pendingRequired.length,
    optionalCandidateCount: candidates.length - requiredCandidates.length,
    pendingCandidateCount: pendingCandidates.length,
    reviewedCandidateCount: candidates.length - pendingCandidates.length,
    invalidTimestampCount: parsed.invalidTimestampCount,
    missingTimestampCount: parsed.missingTimestampCount,
    duplicateHashCount: parsed.duplicateHashes.length,
    skippedEdgesNoTimeCount,
    skippedSwapBridgeSelfTransferCount,
    candidates,
    requiredCandidates,
    pendingRequired,
    pendingCandidates,
    currentBasisHash,
    completedBasisHash,
    isCurrent,
    pass: pendingRequired.length === 0,
    summary
  };
}

export function buildCaseDepositPatternAuditRows(analysis = {}, actionLabel = '') {
  const rows = [
    ['Case Buddy Deposit Pattern Review Audit', '', '', '', '', '', '', '', '', 'Generated by Review high-priority deposit pattern candidates. Audit only; Main Data is the source of truth.'],
    ['Action', actionLabel || 'Review refreshed', 'Entries Parsed', analysis.entriesParsed || 0, 'Edges Checked', analysis.edgesChecked || 0, 'Candidates', analysis.totalCandidateCount || 0, 'Pending Required', analysis.pendingRequiredCount || 0],
    ['Status', analysis.summary || '', 'Required Reviewed', analysis.requiredReviewedCount || 0, 'Required Total', analysis.requiredCandidateCount || 0, 'Optional', analysis.optionalCandidateCount || 0, 'Skipped No Time', analysis.skippedEdgesNoTimeCount || 0],
    ['Omissions', 'Swap/bridge self-transfer edges', analysis.skippedSwapBridgeSelfTransferCount || 0, '', '', '', '', '', '', 'Excluded when a parent/child pair has swapped/bridged notes and one entry has exactly one From and one To address that are the same address.'],
    ['Grade', 'Status', 'Parent Row', 'Parent Label', 'Parent Hash', 'Child Row', 'Child Label', 'Child Hash', 'Delta Minutes', 'Token', 'Amounts', 'Signals', 'Child Q', 'Child Notes']
  ];
  (analysis.candidates || []).forEach((candidate) => {
    rows.push([
      candidate.grade || '',
      candidate.hasCurrentDecision ? candidate.currentReviewStatusLabel : 'Pending',
      candidate.parentRowNumber || '',
      candidate.parentPathLabel || '',
      candidate.parentHash || '',
      candidate.childOutputRowNumber || candidate.childRowNumber || '',
      candidate.childPathLabel || '',
      candidate.childHash || '',
      candidate.deltaMinutesFormatted || '',
      candidate.token || '',
      `${candidate.amountAFormatted || ''} -> ${candidate.amountBFormatted || ''}`,
      (candidate.veryHighSignals || []).join(', '),
      candidate.childQ || '',
      candidate.childNotes || ''
    ]);
  });
  return rows;
}
